import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const api = useApi();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("error"); return; }
      } catch { /* retry */ }
      if (attempts.current >= 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, api]);

  return (
    <div data-testid="payment-success-page" className="pt-40 pb-40 px-4 min-h-[70vh] flex items-center justify-center">
      <div className="max-w-lg w-full text-center card-parchment p-12">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto animate-spin" />
            <h1 className="font-serif text-3xl text-wine mt-6">Confirming your seat…</h1>
            <p className="text-ink-muted mt-2">One moment while we light the candle.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto flicker" />
            <h1 className="font-serif text-4xl text-wine mt-6">Your seat is kept.</h1>
            <p className="text-ink-muted mt-3 leading-relaxed">
              The evening is confirmed. The exact address will reach you privately before the night.
            </p>
            <Link to="/bookings" data-testid="view-bookings-btn" className="btn-gold inline-block mt-8 px-8 py-3 text-sm font-semibold tracking-wide">
              View my evenings
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <h1 className="font-serif text-3xl text-wine mt-2">Still processing</h1>
            <p className="text-ink-muted mt-3">
              Your payment is being confirmed. Check “My evenings” in a moment — it will appear there once settled.
            </p>
            <Link to="/bookings" className="btn-gold inline-block mt-8 px-8 py-3 text-sm font-semibold tracking-wide">
              Go to my evenings
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
